// Marketing · Detalle de lista/segmento: miembros paginados, añadir (correos pegados),
// quitar, editar filtros, duplicar, convertir a estática y exportar CSV.
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ChevronLeft, ChevronRight, Copy, Download, Lock, Pencil, Search, Trash2, UserPlus } from "lucide-react";
import { api } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { formatDate } from "../../../lib/format.ts";
import { Button, Card, EmptyState, ErrorState, Field, Input, Loading, Modal, TableSkeleton, Textarea } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { FilterPanel } from "./FilterPanel.tsx";
import { KindBadge } from "./ListsPage.tsx";
import {
  cleanCriteria, type Criteria, describeCriteria, downloadFile, errMsg, fullName, type MktContact, type MktList, StatusBadge, useMktOptions,
} from "./shared.tsx";

export function ListDetail(): JSX.Element {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isAdmin = Boolean(useMe().data?.admin);
  const opts = useMktOptions();
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => { const t = setTimeout(() => { setQ(qInput.trim()); setPage(1); }, 300); return () => clearTimeout(t); }, [qInput]);

  const list = useQuery<{ list: MktList }>({ queryKey: ["mkt", "list", id], queryFn: () => api.get(`/mkt/lists/${id}`) });
  const members = useQuery<{ rows: MktContact[]; total: number; pageSize: number }>({
    queryKey: ["mkt", "list-members", id, q, page],
    queryFn: () => api.get(`/mkt/lists/${id}/members?page=${page}&pageSize=25&q=${encodeURIComponent(q)}`),
    placeholderData: keepPreviousData,
  });
  const refresh = (): void => { setSelected(new Set()); void qc.invalidateQueries({ queryKey: ["mkt"] }); };

  const dup = useMutation({
    mutationFn: () => api.post<{ list: MktList }>(`/mkt/lists/${id}/duplicate`, {}),
    onSuccess: (r) => { toast.success("Lista duplicada."); refresh(); navigate(`/marketing/contactos/listas/${r.list.id}`); },
    onError: (e) => toast.error(errMsg(e, "No se pudo duplicar.")),
  });
  const convert = useMutation({
    mutationFn: () => api.post<{ added: number }>(`/mkt/lists/${id}/convert`, {}),
    onSuccess: (r) => { toast.success(`Convertida en lista estática con ${r.added} contacto(s).`); refresh(); },
    onError: (e) => toast.error(errMsg(e, "No se pudo convertir.")),
  });
  const del = useMutation({
    mutationFn: () => api.del(`/mkt/lists/${id}`),
    onSuccess: () => { toast.success("Lista eliminada."); refresh(); navigate("/marketing/contactos/listas"); },
    onError: (e) => toast.error(errMsg(e, "No se pudo eliminar.")),
  });
  const remove = useMutation({
    mutationFn: (contactIds: string[]) => api.post<{ removed: number }>(`/mkt/lists/${id}/members/remove`, { contactIds }),
    onSuccess: (r) => { toast.success(`${r.removed} contacto(s) quitados.`); refresh(); },
    onError: (e) => toast.error(errMsg(e, "No se pudieron quitar.")),
  });

  if (list.isLoading) return <Loading />;
  if (list.isError || !list.data) return <ErrorState message={errMsg(list.error, "No se encontró la lista.")} onRetry={() => void list.refetch()} />;
  const l = list.data.list;
  const dynamic = l.kind === "dynamic";
  const rows = members.data?.rows ?? [];
  const total = members.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 25));
  const chips = describeCriteria(l.criteria, opts.data);
  const canManage = isAdmin && !dynamic;

  return (
    <div className="space-y-3">
      <Link to="/marketing/contactos/listas" className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-primary"><ArrowLeft size={14} /> Listas</Link>

      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap"><h2 className="text-xl font-semibold text-ink">{l.name}</h2><KindBadge kind={l.kind} /></div>
            {l.description && <p className="text-sm text-ink-secondary mt-1">{l.description}</p>}
            <p className="text-xs text-ink-muted mt-1">Creada {formatDate(l.createdAt)}{l.createdByName && ` por ${l.createdByName}`}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && <Button onClick={() => setEditing(true)}><Pencil size={16} /> Editar</Button>}
            {canManage && <Button variant="primary" onClick={() => setAdding(true)}><UserPlus size={16} /> Añadir contactos</Button>}
            {isAdmin && dynamic && (
              <Button disabled={convert.isPending} title="Congela los miembros actuales"
                onClick={() => { if (window.confirm("¿Convertir el segmento en lista estática? Los miembros dejarán de recalcularse.")) convert.mutate(); }}>
                <Lock size={16} /> Convertir a estática
              </Button>
            )}
            <Button onClick={() => void downloadFile(`/mkt/lists/${id}/export`, `lista_${l.name.replace(/[^\w-]+/g, "_")}.csv`)}><Download size={16} /> Exportar</Button>
            {isAdmin && <Button onClick={() => dup.mutate()} disabled={dup.isPending}><Copy size={16} /> Duplicar</Button>}
            {isAdmin && <Button variant="danger" onClick={() => { if (window.confirm(`¿Eliminar "${l.name}"? Los contactos no se borran.`)) del.mutate(); }}><Trash2 size={16} /></Button>}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="Contactos" value={l.members} />
          <Stat label="Con email" value={l.withEmail} />
        </div>
        {dynamic && chips.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            <span className="text-xs text-ink-muted mr-1">Filtros:</span>
            {chips.map((c) => <span key={c} className="badge bg-surface-hover text-ink-secondary">{c}</span>)}
          </div>
        )}
      </Card>

      <EditList open={editing} list={l} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); refresh(); }} />
      <AddMembers open={adding} listId={id} onClose={() => setAdding(false)} onDone={() => { setAdding(false); refresh(); }} />

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" aria-hidden />
          <Input className="pl-9" placeholder="Buscar en la lista" value={qInput} onChange={(e) => setQInput(e.target.value)} aria-label="Buscar en la lista" />
        </div>
        {canManage && selected.size > 0 && (
          <Button variant="danger" disabled={remove.isPending}
            onClick={() => { if (window.confirm(`¿Quitar ${selected.size} contacto(s) de la lista?`)) remove.mutate([...selected]); }}>
            Quitar {selected.size} de la lista
          </Button>
        )}
      </div>

      <Card className="overflow-hidden">
        {members.isLoading ? <TableSkeleton rows={6} cols={5} />
          : members.isError ? <ErrorState message={errMsg(members.error, "No se pudieron cargar los miembros.")} onRetry={() => void members.refetch()} />
          : rows.length === 0 ? <EmptyState title="Sin contactos" body={dynamic ? "Ningún contacto cumple los filtros del segmento." : "Añade contactos pegando correos o desde Contactos con la acción masiva."} />
          : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-canvas">
                  {canManage && <th className="th w-10"><input type="checkbox" aria-label="Seleccionar página"
                    checked={rows.every((r) => selected.has(r.id))}
                    onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} /></th>}
                  <th className="th">Nombre</th><th className="th">Empresa</th><th className="th">Estado</th><th className="th text-right">Puntaje</th><th className="th">Propietario</th>
                </tr></thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-surface-hover">
                      {canManage && <td className="td"><input type="checkbox" checked={selected.has(r.id)} aria-label={`Seleccionar ${fullName(r)}`}
                        onChange={() => setSelected((p) => { const n = new Set(p); if (n.has(r.id)) n.delete(r.id); else n.add(r.id); return n; })} /></td>}
                      <td className="td">
                        <Link to={`/contacts/${r.id}`} className="font-medium text-ink hover:text-primary hover:underline">{fullName(r)}</Link>
                        <div className="text-xs text-ink-muted">{r.email ?? "sin email"}</div>
                      </td>
                      <td className="td">{r.accountName ?? <span className="text-ink-muted">—</span>}</td>
                      <td className="td"><StatusBadge status={r.mktStatus} /></td>
                      <td className="td text-right tnum">{r.mktScore}</td>
                      <td className="td whitespace-nowrap">{r.ownerName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
      <div className="flex items-center justify-end gap-2 text-xs text-ink-secondary">
        <Button small disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Página anterior"><ChevronLeft size={14} /></Button>
        <span className="tnum text-ink-muted">Página {page} de {totalPages} · {total.toLocaleString("es-CO")} visibles</span>
        <Button small disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Página siguiente"><ChevronRight size={14} /></Button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="rounded-lg border border-line px-3 py-2">
      <div className="dt-label">{label}</div>
      <div className="text-xl font-semibold text-ink tnum">{value.toLocaleString("es-CO")}</div>
    </div>
  );
}

function EditList({ open, list, onClose, onSaved }: { open: boolean; list: MktList; onClose: () => void; onSaved: () => void }): JSX.Element | null {
  const opts = useMktOptions();
  const [name, setName] = useState(list.name);
  const [description, setDescription] = useState(list.description ?? "");
  const [criteria, setCriteria] = useState<Criteria>(list.criteria ?? {});
  useEffect(() => { if (open) { setName(list.name); setDescription(list.description ?? ""); setCriteria(list.criteria ?? {}); } }, [open, list]);
  const save = useMutation({
    mutationFn: () => api.patch(`/mkt/lists/${list.id}`, {
      name: name.trim(), description: description.trim() || null,
      ...(list.kind === "dynamic" ? { criteria: cleanCriteria(criteria) } : {}),
    }),
    onSuccess: () => { toast.success("Lista actualizada."); onSaved(); },
    onError: (e) => toast.error(errMsg(e, "No se pudo guardar.")),
  });
  const chips = describeCriteria(criteria, opts.data);
  return (
    <Modal open={open} onClose={onClose} title="Editar lista" wide
      footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="primary" disabled={!name.trim() || save.isPending} onClick={() => save.mutate()}>Guardar</Button></>}>
      <div className="space-y-4">
        <Field label="Nombre" required><Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Descripción"><Textarea rows={2} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        {list.kind === "dynamic" && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-1">{chips.map((c) => <span key={c} className="badge bg-surface-hover text-ink-secondary">{c}</span>)}</div>
            <div className="border border-line rounded-lg p-3">
              <FilterPanel value={criteria} options={opts.data} onApply={setCriteria} applyLabel="Usar estos filtros" />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function AddMembers({ open, listId, onClose, onDone }: { open: boolean; listId: string; onClose: () => void; onDone: () => void }): JSX.Element | null {
  const [emails, setEmails] = useState("");
  const [result, setResult] = useState<{ added: number; notFound: string[]; notFoundCount: number; invalid: number } | null>(null);
  useEffect(() => { if (open) { setEmails(""); setResult(null); } }, [open]);
  const add = useMutation({
    mutationFn: () => api.post<{ added: number; notFound: string[]; notFoundCount: number; invalid: number }>(`/mkt/lists/${listId}/members`, { emails }),
    onSuccess: (r) => {
      setResult(r);
      toast.success(`${r.added} contacto(s) añadidos.`);
      if (!r.notFoundCount && !r.invalid) onDone();
    },
    onError: (e) => toast.error(errMsg(e, "No se pudieron añadir.")),
  });
  return (
    <Modal open={open} onClose={result ? onDone : onClose} title="Añadir contactos a la lista"
      footer={<><Button onClick={result ? onDone : onClose}>{result ? "Cerrar" : "Cancelar"}</Button>
        <Button variant="primary" disabled={!emails.trim() || add.isPending} onClick={() => add.mutate()}>{add.isPending ? "Añadiendo…" : "Añadir"}</Button></>}>
      <Field label="Correos" hint="Separados por coma, espacio o salto de línea. Se buscan contactos existentes por correo. Para añadir por filtros usa Contactos → acción masiva.">
        <Textarea rows={6} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="ana@empresa.com&#10;luis@empresa.com" />
      </Field>
      {result && (
        <div className="mt-3 text-sm text-ink-secondary space-y-1">
          <p>Añadidos: <b className="text-ink">{result.added}</b>{result.invalid > 0 && ` · ${result.invalid} texto(s) no son correos válidos`}</p>
          {result.notFoundCount > 0 && (
            <p>No encontrados ({result.notFoundCount}): <span className="text-ink-muted break-all">{result.notFound.join(", ")}{result.notFoundCount > result.notFound.length && "…"}</span></p>
          )}
        </div>
      )}
    </Modal>
  );
}
